import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type {
  AnnotationHandle,
  CaseworkHandle,
  CeilingHandle,
  ColumnHandle,
  ContentTemplateInstanceHandle,
  DrawingRefHandle,
  EdgeBehaviorHandle,
  EdgeModifierHandle,
  ElectricalDeviceHandle,
  ElectricalLayoutHandle,
  FloorFinishHandle,
  FloorHandle,
  MeasurementHandle,
  OpeningHandle,
  PropInstanceHandle,
  RoomLayoutInstanceHandle,
  SelectorHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { MatchExpression } from "building-types/options/matchExpressionTypes";
import type { Paths } from "common/utils/dotNotationTypes";
import type { LineAnchor } from "geom/polyLine2Types";
import type { RemapType } from "./remapDocEntityTypesEnum";

export type RoomRemapEnabledDocEntityHandle =
  | AnnotationHandle
  | CaseworkHandle
  | CeilingHandle
  | ColumnHandle
  | DrawingRefHandle
  | EdgeBehaviorHandle
  | EdgeModifierHandle
  | SelectorHandle
  | ElectricalDeviceHandle
  | ElectricalLayoutHandle
  | FloorHandle
  | FloorFinishHandle
  | MeasurementHandle
  | RoomLayoutInstanceHandle
  | OpeningHandle
  | PropInstanceHandle
  | ContentTemplateInstanceHandle;

export type RoomRemapEnabledEntityType = RoomRemapEnabledDocEntityHandle["type"];
export type RoomRemapEnabledDocEntity<T extends RoomRemapEnabledEntityType = RoomRemapEnabledEntityType> =
  DocEntityTypeToValueType[T] & { edgeId: string; roomId: string };

const roomRemapEnabledDocEntityHandleTypes: { [key in RoomRemapEnabledEntityType]: true } = {
  ANNOTATION: true,
  CASEWORK: true,
  CEILING: true,
  COLUMN: true,
  DRAWING_REF: true,
  EDGE_BEHAVIOR: true,
  EDGE_MODIFIER: true,
  ELECTRICAL_DEVICE: true,
  ELECTRICAL_LAYOUT: true,
  FLOOR: true,
  FLOOR_FINISH: true,
  MEASUREMENT: true,
  OPENING: true,
  PROP_INSTANCE: true,
  ROOM_LAYOUT_INSTANCE: true,
  SELECTOR: true,
  CONTENT_TEMPLATE_INSTANCE: true,
};

export const roomRemapEnabledEntityTypes = Object.keys(
  roomRemapEnabledDocEntityHandleTypes,
) as RoomRemapEnabledEntityType[];

export function isRoomRemapEnabledDocEntityHandle(handle: any): handle is RoomRemapEnabledDocEntityHandle {
  return "type" in handle && roomRemapEnabledDocEntityHandleTypes[handle.type];
}

export function isRoomRemapEnabledEntity(entity: DocEntityBase): entity is RoomRemapEnabledDocEntity {
  return roomRemapEnabledEntityTypes.includes(entity.entityType as any);
}

export interface DocEntityRemap<T extends {}, U> {
  readonly handle: RoomRemapEnabledDocEntityHandle;
  readonly remap: Remap<T>;
  readonly arrayFieldRemaps?: DocEntityArrayFieldRemap<T, U>[];
}

// now repeat for RoomRemap
export interface RoomRemapValues {
  readonly roomId: string | undefined; //satisfy strict null for parentId, which can be undefined
}

export interface RoomRemap<T> {
  readonly type: RemapType.ROOM;
  readonly roomIdAccessor: Paths<T, string | undefined> | Paths<T, string | undefined>[];
}

export interface EdgeRemapValues {
  readonly roomId: string | undefined; //satisfy strict null for parentId, which can be undefined
  readonly edgeId: string;
}

export interface EdgeRemap<T> {
  readonly type: RemapType.EDGE;
  readonly roomIdAccessor: Paths<T, string | undefined> | Paths<T, string | undefined>[];
  readonly edgeIdAccessor: Paths<T, string>;
}

export type MatchExpressionParamRemapValues = MatchExpression<ParamRemapValues>;

export interface ParamRemapValues {
  readonly roomId: string | undefined; //satisfy strict null for parentId, which can be undefined
  readonly edgeId: string | undefined;
  readonly param: number | undefined;
  readonly paramNormalized: boolean;
  readonly paramAnchor: LineAnchor | undefined;
  readonly lateralOffset: number | null | undefined;
  readonly lateralOffsetNormalized: boolean | undefined;
}

export interface ParamRemap<T> {
  readonly type: RemapType.PARAM;
  readonly roomIdAccessor: Paths<T, string | undefined>;
  readonly edgeIdAccessor: Paths<T, string>;
  readonly paramAccessor: Paths<T, number | MatchExpression<number>>;
  readonly paramNormalizedAccessor: Paths<T, boolean | MatchExpression<boolean>>;
  readonly paramAnchorAccessor?: Paths<T, string>;
  readonly lateralOffsetAccessor?: Paths<T, number>;
  readonly lateralOffsetNormalizedAccessor?: Paths<T, boolean>;
}

type Remap<T> = RoomRemap<T> | EdgeRemap<T> | ParamRemap<T>;

export interface BaseArrayFieldRemap {
  readonly index: number;
}
export interface ArrayFieldRoomRemap<T> extends BaseArrayFieldRemap {
  type: RemapType.ROOM;
  readonly roomIdAccessor: Paths<T, string | undefined>;
}

export interface ArrayFieldEdgeRemapValues {
  readonly edgeId: string;
}

export interface ArrayFieldEdgeRemap<T> extends BaseArrayFieldRemap {
  readonly type: RemapType.EDGE;
  readonly edgeIdAccessor: Paths<T, string>;
}

export interface ArrayFieldParamRemap<T> extends BaseArrayFieldRemap {
  readonly type: RemapType.PARAM;
  readonly roomIdAccessor: Paths<T, string | undefined>;
  readonly edgeIdAccessor: Paths<T, string>;
  readonly paramAccessor: Paths<T, number | MatchExpression<number>>;
  readonly paramNormalizedAccessor: Paths<T, boolean | MatchExpression<boolean>>;
  readonly paramAnchorAccessor?: Paths<T, string>;
  readonly lateralOffsetAccessor?: Paths<T, number>;
  readonly lateralOffsetNormalizedAccessor?: Paths<T, boolean>;
}

export type DocEntityFieldRemap<T> = ArrayFieldRoomRemap<T> | ArrayFieldEdgeRemap<T> | ArrayFieldParamRemap<T>;

export interface DocEntityArrayFieldRemap<T, U, V = keyof T> {
  readonly field: V; // needed when get returns nulls
  readonly get: (docEntityArrayField: U[]) => (DocEntityFieldRemap<U> | null)[];
  readonly deleteDocEntityIfArrayFieldEmpty?: boolean; // controls whether the parent entity is removed if no remaps are found for these nested entities
}

export type DeduplicationHashTable = { [dstRoomIdAndEdgeId: string]: EntityHashTable };
type EntityHashTable = { [entityHash: string]: true };

export type RemapResult = RemapResultNoExp | RemapResultMatchExpr;
export type RemapResultNoExp = RemapResultDefault | RemapResultWithLateralOffset;
export type RemapResultMatchExpr = RemapResultDefaultMatchExpr | RemapResultWithLateralOffsetMatchExpr;

interface RemapResultBase {
  readonly type: "DEFAULT" | "WITH_LATERAL_OFFSET" | "DEFAULT_MATCH_EXPR" | "WITH_LATERAL_OFFSET_MATCH_EXPR";
  readonly roomId: string;
  readonly edgeId: string;
  readonly param: number | MatchExpression<number>;
  readonly paramNormalized: boolean | MatchExpression<boolean>;
}

export interface RemapResultDefault extends RemapResultBase {
  readonly type: "DEFAULT";
  readonly param: number;
  readonly paramNormalized: boolean;
}

export interface RemapResultWithLateralOffset extends RemapResultBase {
  readonly type: "WITH_LATERAL_OFFSET";
  readonly lateralOffset: number;
  readonly param: number;
  readonly paramNormalized: boolean;
}

export interface RemapResultDefaultMatchExpr extends RemapResultBase {
  readonly type: "DEFAULT_MATCH_EXPR";
  readonly param: MatchExpression<number>;
  readonly paramNormalized: MatchExpression<boolean>;
}

export interface RemapResultWithLateralOffsetMatchExpr extends RemapResultBase {
  readonly type: "WITH_LATERAL_OFFSET_MATCH_EXPR";
  readonly param: MatchExpression<number>;
  readonly paramNormalized: MatchExpression<boolean>;
  readonly lateralOffset: number;
}
